import { useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';

import NotSpecifiedRoleDialog from '@/components/shared/Dialog/NotSpecifiedRoleDialog';
import EditPage from '@/components/shared/Layouts/EditPage/EditPage';
import type { BudgetGroup } from '@/components/shared/Layouts/EditPage/EditPage.types';
import { useOrganizationId } from '@/contexts/OrganizationContext';
import { useUsersAutocomplete } from '@/hooks/autocomplete/useUsersAutocomplete';
import { useBudgetGetDistributorGroupInfo } from '@/hooks/useBudgetGetDistributorGroupInfo';
import { useBudgetGroups } from '@/hooks/useBudgetGroups';
import { useBudgetGroupUserMembership } from '@/hooks/useBudgetGroupUserMembership';
import { useDistributorGetDistributorGroupInfo } from '@/hooks/useDistributorGetDistributorGroupInfo';
import { useManageBudgetGroupUsers } from '@/hooks/useManageBudgetGroupUsers';
import { usePendingRoleSelection } from '@/hooks/usePendingRoleSelection';
import { useSnackbar } from '@/hooks/useSnackbar';
import { useSwapDistributorRoleForBudgetGroup } from '@/hooks/useSwapDistributorRoleForBudgetGroup';
import { DEFAULT_PAGE_SIZE, useTablePaginationState } from '@/hooks/useTablePaginationState';
import { useUsersByBudgetGroup } from '@/hooks/useUsersByBudgetGroup';
import { ROUTES } from '@/routes';

interface LocationState {
  budgetGroup?: BudgetGroup;
  returnSearchParams?: string;
}

function EditBudgetGroup() {
  const { id } = useParams<{ id: string }>();
  const budgetGroupId = id ? parseInt(id, 10) : undefined;
  const { organizationId } = useOrganizationId();
  const location = useLocation();
  const snackbar = useSnackbar();

  const { pagination, setPagination } = useTablePaginationState({
    defaultPageSize: DEFAULT_PAGE_SIZE,
  });

  const [debouncedValue, setDebouncedValue] = useState<string>('');
  const { markPending, resolvePending, isBlocked, proceed, reset } = usePendingRoleSelection<string>();

  const {
    data: usersGroupData,
    isLoading,
    isRefetching,
    error,
  } = useUsersByBudgetGroup(budgetGroupId, pagination.pageIndex + 1, pagination.pageSize);
  const { items: users, totalCount } = usersGroupData || {};

  const {
    addUsers,
    updateUser,
    removeUser,
    removeUsers,
    loading: isManagingUsers,
    removeMutation,
    removeUsersBatchMutation,
  } = useManageBudgetGroupUsers({ snackbar });

  const { swapDistributorRole, isSwappingRole } = useSwapDistributorRoleForBudgetGroup();
  const { getDistributorGroupInfo } = useBudgetGetDistributorGroupInfo();
  const { getDistributorGroupInfo: getDistributorGroupDistributorInfo } = useDistributorGetDistributorGroupInfo();

  const { budgetGroups } = useBudgetGroups(1, 50, 'name');

  // Get budget group from location state first, then fall back to fetching from hook
  const state = location.state as LocationState | null;

  const budgetGroup =
    state?.budgetGroup ??
    (budgetGroups?.items && budgetGroupId ? (budgetGroups.items.find((ag) => ag.id === budgetGroupId) ?? null) : null);

  const usersAutocompleteQuery = useUsersAutocomplete({
    organizationId,
    filter: debouncedValue,
    enabled: !isManagingUsers,
  });

  const usersIdsForAutocomplete =
    usersAutocompleteQuery.data?.map((u) => u.email).filter((email): email is string => email !== null && email !== undefined) ?? [];

  const membershipQuery = useBudgetGroupUserMembership({
    organizationId,
    groupId: budgetGroupId ?? 0,
    userIds: usersIdsForAutocomplete,
    enabled: !isManagingUsers && usersIdsForAutocomplete.length > 0,
  });

  const usersList =
    users?.map((u) => ({
      userId: u.userId ?? null,
      email: (u.email || u.userId) ?? null,
      fullName: u.fullName ?? null,
      role: u.role ?? '',
    })) ?? [];

  const now = new Date();
  const currentDate = `${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}${now.getFullYear()}`;

  return (
    <>
      <EditPage
        route={ROUTES.BUDGETS}
        group={budgetGroup}
        organizationId={organizationId}
        isManagingUsers={isManagingUsers}
        pagination={{ state: pagination, onChange: setPagination }}
        breadcrumbItems={{
          prevPathLabel: 'Budgets',
          currentPathLabel: 'Manage Budget',
          currentPath: `/budgets/manage/${budgetGroupId}`,
        }}
        sectionTitle={{
          testId: 'user-details-title',
          title: budgetGroup?.name || 'Budget Group Name',
        }}
        autocompleteProps={{
          usersData: usersAutocompleteQuery,
          membershipData: membershipQuery,
          filter: debouncedValue,
          onFilteredSearch: setDebouncedValue,
          disabled: !budgetGroupId || isManagingUsers,
          onAddUsers: async (groupId, usersId, group) => {
            const budgetGroup = group as BudgetGroup;
            const result = await addUsers(groupId, usersId, {
              budgetName: budgetGroup?.name ?? null,
              isActive: budgetGroup?.isActive,
            });
            if (result) markPending(usersId);
            return result;
          },
        }}
        error={error}
        table={{
          testId: 'budget-users-table',
          ariaLabel: 'Budget users list',
          noDataMessage: 'No users assigned.',
          data: usersList,
          totalCount: totalCount ?? 0,
          onRemoveSingleUser: async (groupId, user) => {
            const result = await removeUser(groupId, user.userId!, user.role!);
            if (result) resolvePending([user.userId!]);
            return result;
          },
          onRemoveUsersInBatch: async (groupId, users) => {
            const result = await removeUsers(
              groupId,
              users.map((u) => ({ userId: u.userId!, role: u.role! })),
            );
            if (result) resolvePending(users.map((u) => u.userId!));
            return result;
          },
          isRefetching,
          isLoading,
          removeUserMutation: removeMutation,
          removeUsersBatchMutation,
          isSwappingRole,
          onUpdateRole: async (groupId, userId, newRole) => {
            const result = await updateUser(groupId, userId, newRole);
            if (result) resolvePending([userId]);
            return result;
          },
          getDistributorGroupConflict: (orgId, userId) => getDistributorGroupDistributorInfo(orgId, userId),
          getBudgetGroupConflict: (orgId, userId) => getDistributorGroupInfo(orgId, userId),
          onSwapConfirm: async (groupId, swapData) => {
            const result = await swapDistributorRole({
              organizationId,
              params: {
                userId: swapData.userId,
                currentBudgetGroupId: swapData.currentGroupId,
                newBudgetGroupId: groupId,
              },
            });
            if (result) resolvePending([swapData.userId]);
            return result;
          },
        }}
        exportConfig={{
          url: `/api/organizations/${organizationId}/budget-groups/${budgetGroupId}/export-users`,
          fileName: `BG_${budgetGroup?.name}_Users_${currentDate}.xlsx`,
        }}
      />
      <NotSpecifiedRoleDialog open={isBlocked} onConfirm={proceed} onCancel={reset} />
    </>
  );
}

export default EditBudgetGroup;
